import React, {
    useState,
    createContext,
    useEffect,
    useContext,
    useMemo,
    useRef,
} from 'react'
import { LocalStorageKeys } from '~config/constants'
import { useUserContext } from '~config/user-context'

import {
    FilterOp,
    AuthRoles,
    useGetClinicQuery,
    ClinicFieldsFragment,
    useGetClinicAdminQuery,
    useGetClinicAccessForUserQuery,
    useCountClinicsForUserAdminQuery,
    ClinicGroupFieldsFragment,
    useGetClinicGroupQuery,
    useCountClinicGroupsForUserQuery,
    ClinicAdminPermissionFieldsFragment,
    useGetClinicGroupUserByGroupAndUserQuery,
} from '~graphql/generated/graphql'
import { getEffectivePermissions } from '~components/manage/users/detailed/permissions/helpers'

type ClinicProviderType = {
    clinic: ClinicFieldsFragment | undefined
    hasMultipleClinics: boolean
    setClinic: (e: ClinicFieldsFragment | undefined) => void
    /**
     * Switch the active clinic by id without replacing the currently-rendered
     * clinic until the new one's full data has loaded. This keeps the app
     * shell (sidebar/header) stable during a swap and avoids rendering pages
     * with a half-populated clinic. Use this for in-app clinic hot-swapping.
     */
    selectClinicById: (id: string) => void
    clinicGroup: ClinicGroupFieldsFragment | undefined
    setClinicGroup: (e: ClinicGroupFieldsFragment | undefined) => void
    /**
     * Enter group (enterprise) mode for a clinic group by id, leaving clinic
     * mode if it's active. Use when only the id is known (e.g. a notification
     * deep link); `loading` stays true until the group and its permissions
     * have loaded, so group-scoped pages render a loader instead of empty.
     */
    selectClinicGroupById: (id: string) => void
    role: AuthRoles | undefined
    permissions: ClinicAdminPermissionFieldsFragment[]
    /**
     * Whether Daily Huddle financial figures (revenue/earnings) should be
     * hidden for the logged-in user. True when either the clinic-wide setting
     * or the user's own clinic_admin setting opts out of financials.
     */
    shouldHideDailyHuddleFinancials: boolean
    clinicInStorage: boolean | undefined
    /** True while a different clinic has been selected and is still loading. */
    isSwitchingClinic: boolean
    loading: boolean
    /**
     * False until the role/permissions queries for the active clinic or
     * group have settled. Empty `permissions` before this is "not loaded
     * yet", not "this user has no access".
     */
    arePermissionsReady: boolean
}

export const ClinicContext = createContext<ClinicProviderType>({
    clinic: undefined,
    hasMultipleClinics: false,
    setClinic: () => {},
    selectClinicById: () => {},
    clinicGroup: undefined,
    setClinicGroup: () => {},
    selectClinicGroupById: () => {},
    role: undefined,
    permissions: [],
    shouldHideDailyHuddleFinancials: false,
    clinicInStorage: undefined,
    isSwitchingClinic: false,
    loading: false,
    arePermissionsReady: false,
})

export const ClinicProvider = ({ children }: { children: React.ReactNode }) => (
    <ClinicContext.Provider value={{ ...useClinicValues() }}>
        {children}
    </ClinicContext.Provider>
)

/**
 * Re-provides the clinic context scoped to one clinic, for editors opened
 * outside that clinic's own pages (a group standardization page, the admin
 * onboarding wizard). Those editors call `useClinic()`; clearing
 * `clinicGroup` makes them run in clinic mode for `clinic`. Role and
 * permissions are inherited from the surrounding context.
 */
export function ClinicScopeProvider({
    clinic,
    children,
}: {
    clinic: ClinicFieldsFragment | undefined
    children: React.ReactNode
}) {
    const existing = useClinic()
    const value = useMemo(
        () => ({ ...existing, clinic, clinicGroup: undefined }),
        [existing, clinic],
    )
    return (
        <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>
    )
}

function useClinicValues(): ClinicProviderType {
    const { userId } = useUserContext()
    const [initialSelection] = useState(() => getInitialClinicSelection())

    const [clinic, setClinic] = useState<ClinicFieldsFragment | undefined>(
        () => getPersistedSelection(userId).clinic,
    )
    const [clinicGroup, setClinicGroup] = useState<
        ClinicGroupFieldsFragment | undefined
    >(() => getPersistedSelection(userId).clinicGroup)

    const [hasMultipleClinics, setHasMultipleClinics] = useState<boolean>(false)

    const [clinicInStorage, setClinicInStorage] = useState<string | undefined>(
        initialSelection.clinicId,
    )
    const [clinicGroupInStorage, setClinicGroupInStorage] = useState<
        string | undefined
    >(initialSelection.clinicGroupId)

    const displayedClinicIdRef = useRef<string | undefined>(undefined)
    const displayedGroupIdRef = useRef<string | undefined>(undefined)

    const handleSetClinic = (clinic: ClinicFieldsFragment | undefined) => {
        const organization = localStorage.getItem(
            LocalStorageKeys.selectedOrganization,
        )

        if (clinic) {
            localStorage.setItem(LocalStorageKeys.selectedClinic, clinic.id)
            setClinicInStorage(clinic.id)
            if (organization)
                localStorage.removeItem(LocalStorageKeys.selectedOrganization)
        } else {
            localStorage.removeItem(LocalStorageKeys.selectedClinic)
            setClinicInStorage(undefined)
        }
        persistSelection(userId, { clinic })
        setClinic(clinic)
    }

    const handleSelectClinicById = (id: string) => {
        if (!id) return
        const organization = localStorage.getItem(
            LocalStorageKeys.selectedOrganization,
        )
        if (organization)
            localStorage.removeItem(LocalStorageKeys.selectedOrganization)

        localStorage.setItem(LocalStorageKeys.selectedClinic, id)
        // Update only the stored id; `clinic` state is left untouched so the
        // previously-loaded clinic keeps rendering. The getClinic query below
        // re-runs for the new id and atomically replaces `clinic` once the
        // full data arrives.
        setClinicInStorage(id)
    }

    const handleSetClinicGroup = (
        clinicGroup: ClinicGroupFieldsFragment | undefined,
    ) => {
        if (clinicGroup) {
            localStorage.setItem(
                LocalStorageKeys.selectedClinicGroup,
                clinicGroup.id,
            )
            setClinicGroupInStorage(clinicGroup.id)
        } else {
            localStorage.removeItem(LocalStorageKeys.selectedClinicGroup)
            setClinicGroupInStorage(undefined)
        }
        persistSelection(userId, { clinicGroup })
        setClinicGroup(clinicGroup)
    }

    const handleSelectClinicGroupById = (id: string) => {
        if (!id) return
        localStorage.setItem(LocalStorageKeys.selectedClinicGroup, id)
        localStorage.removeItem(LocalStorageKeys.selectedClinic)
        localStorage.removeItem(LocalStorageKeys.selectedOrganization)

        persistSelection(userId, { clinic: undefined })
        setClinic(undefined)
        setClinicInStorage(undefined)

        // Drop a different, previously-loaded group so it isn't rendered
        // under the new group's pages while the getClinicGroup query loads.
        if (clinicGroup && clinicGroup.id !== id) {
            persistSelection(userId, { clinicGroup: undefined })
            setClinicGroup(undefined)
        }
        setClinicGroupInStorage(id)
    }

    const { data: clinicData, loading: clinicLoading } = useGetClinicQuery({
        variables: {
            id: clinicInStorage || '',
        },
        onError: () => {
            // Keep an already-rendered clinic through a transient refetch
            // error. Only drop a selection that never loaded.
            if (displayedClinicIdRef.current) return
            persistSelection(userId, { clinic: undefined })
            setClinic(undefined)
            setClinicInStorage(undefined)
        },
        skip: !clinicInStorage || !userId,
    })

    const { data: clinicGroupData, loading: clinicGroupLoading } =
        useGetClinicGroupQuery({
            variables: {
                id: clinicGroupInStorage || '',
            },
            onError: () => {
                if (displayedGroupIdRef.current) return
                persistSelection(userId, { clinicGroup: undefined })
                setClinicGroup(undefined)
                setClinicGroupInStorage(undefined)
            },
            skip: !clinicGroupInStorage || !userId,
        })

    // Prefer query data on the first paint (Apollo cache survives provider
    // remounts) so navigation doesn't flash an empty clinic. During a hot
    // swap, keep the previously rendered clinic until the new one arrives.
    const isSwitchingClinic =
        !!clinic && !!clinicInStorage && clinic.id !== clinicInStorage
    const displayedClinic: ClinicFieldsFragment | undefined = isSwitchingClinic
        ? clinic
        : ((clinicData?.clinic as ClinicFieldsFragment | undefined) ??
          (clinicInStorage ? clinic : undefined))
    const displayedClinicGroup: ClinicGroupFieldsFragment | undefined =
        (clinicGroupData?.clinicGroup as
            | ClinicGroupFieldsFragment
            | undefined) ??
        (clinicGroupInStorage ? clinicGroup : undefined)

    displayedClinicIdRef.current = displayedClinic?.id
    displayedGroupIdRef.current = displayedClinicGroup?.id

    const { data: clinicAdminData } = useGetClinicAdminQuery({
        variables: {
            clinicId: displayedClinic?.id || '',
            userId: userId || '',
        },
        skip: !displayedClinic?.id || !userId,
    })

    const {
        data: clinicGroupUserData,
        loading: clinicGroupUserLoading,
    } = useGetClinicGroupUserByGroupAndUserQuery({
        variables: {
            clinicGroupId: displayedClinicGroup?.id || '',
            userId: userId || '',
        },
        skip: !displayedClinicGroup?.id || !userId,
    })

    // Direct clinic membership, or access derived from a group that contains
    // this clinic. Direct membership wins. Group users do not get a
    // clinic_admins row.
    const {
        data: clinicAccessData,
        loading: clinicAccessLoading,
    } = useGetClinicAccessForUserQuery({
        variables: {
            clinicId: displayedClinic?.id || '',
            userId: userId || '',
        },
        skip: !displayedClinic?.id || !userId || !!displayedClinicGroup,
    })

    const { data: adminClinicsCount } = useCountClinicsForUserAdminQuery({
        variables: {
            userId: userId || '',
        },
        skip: !userId,
    })

    const { data: adminClinicGroupsCount } = useCountClinicGroupsForUserQuery({
        variables: {
            userId: userId || '',
        },
        skip: !userId,
    })

    /* Clinic state flow START */
    // Boot from an explicit ?clinicId= / ?clinicGroupId= first (used by
    // deep links such as Ctrl+click "open in new tab" from select-clinic),
    // otherwise fall back to localStorage. A clinicId in the URL also clears
    // group mode — otherwise the tab would still render as enterprise even
    // with the right clinic id stored (and vice versa for clinicGroupId).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const clinicIdFromUrl = params.get('clinicId')
        const clinicGroupIdFromUrl = params.get('clinicGroupId')

        if (clinicIdFromUrl) {
            localStorage.setItem(LocalStorageKeys.selectedClinic, clinicIdFromUrl)
            localStorage.removeItem(LocalStorageKeys.selectedClinicGroup)
            localStorage.removeItem(LocalStorageKeys.selectedOrganization)
            setClinicInStorage(clinicIdFromUrl)
            setClinicGroupInStorage(undefined)
            return
        }

        if (clinicGroupIdFromUrl) {
            localStorage.removeItem(LocalStorageKeys.selectedClinic)
            localStorage.removeItem(LocalStorageKeys.selectedOrganization)
            setClinicInStorage(undefined)
            return
        }

        const clinicInStorage = localStorage.getItem(
            LocalStorageKeys.selectedClinic,
        )
        setClinicInStorage(clinicInStorage || undefined)
    }, [])

    // If clinic data received, set clinic state. Only update once the full
    // clinic is present — Apollo briefly reports `undefined` data while a
    // newly selected clinic loads, and clearing here would unmount the app
    // shell mid-swap. Clearing the selection is handled explicitly by
    // `handleSetClinic` and the query's `onError`.
    useEffect(() => {
        if (clinicData?.clinic) {
            persistSelection(userId, {
                clinic: clinicData.clinic as ClinicFieldsFragment,
            })
            setClinic(clinicData.clinic as ClinicFieldsFragment)
        }
    }, [clinicData])
    /* Clinic state flow END */

    /* Clinic group state flow START */
    // Check in storage for clinic group when loaded. Skipped when ?clinicId=
    // already cleared group mode above — both effects run after the first
    // paint, so this one must not re-hydrate a stale group over a deep link.
    // Mirrors the clinic effect: ?clinicGroupId= clears clinic mode.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const clinicIdFromUrl = params.get('clinicId')
        if (clinicIdFromUrl) return

        const clinicGroupIdFromUrl = params.get('clinicGroupId')
        if (clinicGroupIdFromUrl) {
            localStorage.setItem(
                LocalStorageKeys.selectedClinicGroup,
                clinicGroupIdFromUrl,
            )
            localStorage.removeItem(LocalStorageKeys.selectedClinic)
            localStorage.removeItem(LocalStorageKeys.selectedOrganization)
            setClinicGroupInStorage(clinicGroupIdFromUrl)
            setClinicInStorage(undefined)
            return
        }

        const clinicGroupInStorage = localStorage.getItem(
            LocalStorageKeys.selectedClinicGroup,
        )
        setClinicGroupInStorage(clinicGroupInStorage || undefined)
    }, [])

    // If clinic group data received, set clinic group state. Don't clear on
    // a transient `undefined` (skip/refetch) — that would drop enterprise
    // mode and wipe permissions mid-navigation.
    useEffect(() => {
        if (clinicGroupData?.clinicGroup) {
            persistSelection(userId, {
                clinicGroup:
                    clinicGroupData.clinicGroup as ClinicGroupFieldsFragment,
            })
            setClinicGroup(
                clinicGroupData.clinicGroup as ClinicGroupFieldsFragment,
            )
        }
    }, [clinicGroupData])
    /* Clinic group state flow END */

    useEffect(() => {
        const userClinics = adminClinicsCount?.countClinicsForUserAdmin || 0
        const groupsCount =
            adminClinicGroupsCount?.countClinicGroupsForUser || 0
        setHasMultipleClinics(Boolean(userClinics + groupsCount > 1))
    }, [adminClinicsCount, adminClinicGroupsCount])

    const { role, permissions, arePermissionsReady } = resolveClinicAccess({
        userId,
        clinic: displayedClinic,
        clinicGroup: displayedClinicGroup,
        clinicGroupUserData,
        clinicGroupUserLoading,
        clinicAccessData,
        clinicAccessLoading,
    })

    const shouldHideDailyHuddleFinancials =
        (displayedClinic?.settings?.should_hide_daily_huddle_financials ??
            false) ||
        (clinicAdminData?.getClinicAdmin?.should_hide_daily_huddle_financials ??
            false)

    return {
        clinic: displayedClinic,
        setClinic: handleSetClinic,
        selectClinicById: handleSelectClinicById,
        hasMultipleClinics,
        clinicGroup: displayedClinicGroup,
        setClinicGroup: handleSetClinicGroup,
        selectClinicGroupById: handleSelectClinicGroupById,
        role,
        permissions,
        shouldHideDailyHuddleFinancials,
        clinicInStorage: Boolean(clinicInStorage),
        isSwitchingClinic,
        loading:
            (!displayedClinic && !!clinicInStorage && clinicLoading) ||
            (!displayedClinicGroup &&
                !!clinicGroupInStorage &&
                clinicGroupLoading),
        arePermissionsReady,
    }
}

export const useClinic = () => {
    return useContext(ClinicContext)
}

/**
 * Survives ClinicProvider remounts so Gatsby navigation doesn't drop the
 * currently rendered clinic/group while Apollo re-attaches. Keyed by user so
 * a sign-out/sign-in in the same tab (shared clinic workstation) never
 * inherits the previous user's selection.
 */
let persistedSelection: {
    userId: string
    clinic?: ClinicFieldsFragment
    clinicGroup?: ClinicGroupFieldsFragment
} = { userId: '' }

function getPersistedSelection(userId: string): {
    clinic?: ClinicFieldsFragment
    clinicGroup?: ClinicGroupFieldsFragment
} {
    return persistedSelection.userId === userId ? persistedSelection : {}
}

function persistSelection(
    userId: string,
    patch: Pick<typeof persistedSelection, 'clinic' | 'clinicGroup'>,
): void {
    persistedSelection =
        persistedSelection.userId === userId
            ? { ...persistedSelection, ...patch }
            : { userId, ...patch }
}

function getInitialClinicSelection(): {
    clinicId?: string
    clinicGroupId?: string
} {
    if (typeof window === 'undefined') return {}

    const params = new URLSearchParams(window.location.search)
    const clinicIdFromUrl = params.get('clinicId')
    if (clinicIdFromUrl) {
        localStorage.setItem(LocalStorageKeys.selectedClinic, clinicIdFromUrl)
        localStorage.removeItem(LocalStorageKeys.selectedClinicGroup)
        localStorage.removeItem(LocalStorageKeys.selectedOrganization)
        return { clinicId: clinicIdFromUrl }
    }

    const clinicGroupIdFromUrl = params.get('clinicGroupId')
    if (clinicGroupIdFromUrl) {
        localStorage.setItem(
            LocalStorageKeys.selectedClinicGroup,
            clinicGroupIdFromUrl,
        )
        localStorage.removeItem(LocalStorageKeys.selectedClinic)
        localStorage.removeItem(LocalStorageKeys.selectedOrganization)
        return { clinicGroupId: clinicGroupIdFromUrl }
    }

    return {
        clinicId:
            localStorage.getItem(LocalStorageKeys.selectedClinic) || undefined,
        clinicGroupId:
            localStorage.getItem(LocalStorageKeys.selectedClinicGroup) ||
            undefined,
    }
}

type ClinicGroupUserQueryData = {
    clinicGroupUserByGroupAndUser?: {
        role?: AuthRoles | null
        permissions?: ClinicAdminPermissionFieldsFragment[] | null
    } | null
} | null | undefined

type ClinicAccessQueryData = {
    clinicAccessForUser?: {
        role?: AuthRoles | null
        permissions?: ClinicAdminPermissionFieldsFragment[] | null
    } | null
} | null | undefined

/**
 * Derive role/permissions from the live query results instead of copying
 * them into state. A useEffect that wrote `permissions: []` whenever the
 * source data was briefly undefined (skip, refetch, remount) is what made
 * gated pages flash Access Restricted.
 *
 * Clinic mode reads `clinicAccessForUser`, which already prefers a direct
 * clinic membership and otherwise maps the user's group role onto clinic
 * permissions.
 */
function resolveClinicAccess({
    userId,
    clinic,
    clinicGroup,
    clinicGroupUserData,
    clinicGroupUserLoading,
    clinicAccessData,
    clinicAccessLoading,
}: {
    userId: string
    clinic: ClinicFieldsFragment | undefined
    clinicGroup: ClinicGroupFieldsFragment | undefined
    clinicGroupUserData: ClinicGroupUserQueryData
    clinicGroupUserLoading: boolean
    clinicAccessData: ClinicAccessQueryData
    clinicAccessLoading: boolean
}): {
    role: AuthRoles | undefined
    permissions: ClinicAdminPermissionFieldsFragment[]
    arePermissionsReady: boolean
} {
    if (!userId) {
        return {
            role: undefined,
            permissions: [],
            arePermissionsReady: false,
        }
    }

    if (clinicGroup) {
        const groupUser = clinicGroupUserData?.clinicGroupUserByGroupAndUser
        const role = groupUser?.role ?? undefined
        return {
            role,
            permissions: getEffectivePermissions(
                groupUser?.permissions || [],
                role,
                true,
            ),
            arePermissionsReady: !clinicGroupUserLoading,
        }
    }

    if (!clinic) {
        return {
            role: undefined,
            permissions: [],
            arePermissionsReady: true,
        }
    }

    if (clinicAccessLoading) {
        return {
            role: undefined,
            permissions: [],
            arePermissionsReady: false,
        }
    }

    const access = clinicAccessData?.clinicAccessForUser
    const role = access?.role ?? undefined
    return {
        role,
        permissions: getEffectivePermissions(
            access?.permissions || [],
            role,
            false,
        ),
        arePermissionsReady: true,
    }
}
